import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTenantUserInfo } from '../../context/userInfoProvider';
import { S3url } from '../../components/TenantApp/s3url';

type StorageItem = {
  id: string;
  description: string;
  photo: string;
};

type SortOption = '' | 'Newest' | 'Alphabetical';

const pageContainer: React.CSSProperties = {
  minHeight: '100vh',
  backgroundColor: '#261136',
  padding: 24,
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  fontFamily:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

const shellCard: React.CSSProperties = {
  width: '100%',
  maxWidth: 900,
  background:
    'radial-gradient(circle at top, #3A1F52 0, #170824 45%, #12061f 100%)',
  borderRadius: 16,
  padding: 24,
  boxSizing: 'border-box',
  boxShadow: '0 18px 45px rgba(0,0,0,0.45)',
  color: '#F5F5FA',
};

const headerRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  marginBottom: 16,
  flexWrap: 'wrap',
};

const titleStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 600,
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 13,
  opacity: 0.8,
};

const sortBlock: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const sortLabel: React.CSSProperties = {
  fontSize: 13,
  color: '#E2E4FF',
};

const sortSelect: React.CSSProperties = {
  backgroundColor: '#1F0F30',
  borderRadius: 6,
  border: '1px solid #61518A',
  padding: '6px 8px',
  color: '#F5F5FA',
  fontSize: 13,
};

const refreshButton: React.CSSProperties = {
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid #61518A',
  backgroundColor: '#2A1641',
  color: '#F5F5FA',
  fontSize: 12,
  cursor: 'pointer',
};

const grid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
  gap: 16,
  marginTop: 8,
};

const itemCard: React.CSSProperties = {
  backgroundColor: '#FFFFFF',
  borderRadius: 8,
  overflow: 'hidden',
  boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
  color: '#111827',
  display: 'flex',
  flexDirection: 'column',
};

const imageWrapper: React.CSSProperties = {
  width: '100%',
  paddingTop: '100%',
  position: 'relative',
};

const imageStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

const itemBody: React.CSSProperties = {
  padding: 10,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  flexGrow: 1,
};

const itemTitle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: 14,
};

const itemMetaRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const itemMeta: React.CSSProperties = {
  fontSize: 12,
  opacity: 0.8,
};

const selectButton: React.CSSProperties = {
  padding: '4px 8px',
  borderRadius: 4,
  border: 'none',
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
};

const selectButtonSelected: React.CSSProperties = {
  backgroundColor: 'green',
  color: '#FFF',
};

const selectButtonDefault: React.CSSProperties = {
  backgroundColor: '#7B1FA2',
  color: '#FFF',
};

const footer: React.CSSProperties = {
  marginTop: 20,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
};

const primaryButton: React.CSSProperties = {
  padding: '10px 16px',
  borderRadius: 6,
  border: 'none',
  backgroundColor: '#7B1FA2',
  color: '#FFF',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const selectionSummary: React.CSSProperties = {
  fontSize: 13,
  opacity: 0.9,
};

const emptyState: React.CSSProperties = {
  marginTop: 32,
  textAlign: 'center',
  fontSize: 14,
  opacity: 0.85,
};

const TenantNewDeliveryPage: React.FC = () => {
  const history = useHistory();
  const { dataSourceStorage = [], fetchData } = useTenantUserInfo() as {
    dataSourceStorage?: StorageItem[];
    fetchData: () => Promise<void>;
  };

  const [sortBy, setSortBy] = useState<SortOption>('Newest');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchData().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items = useMemo(() => {
    const copy = [...dataSourceStorage];
    if (sortBy === 'Alphabetical') {
      copy.sort((a, b) => a.description.localeCompare(b.description));
    }
    return copy;
  }, [dataSourceStorage, sortBy]);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      await fetchData();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSubmit = () => {
    const selected = Array.from(selectedIds);
    if (selected.length === 0) {
      window.alert('Please select at least one item.');
      return;
    }

    const payload = {
      selected,
      type: 'DELIVERY' as const,
      id: selected,
    };

    history.push('/tenant/neworder/schedule', payload);
  };

  return (
    <div style={pageContainer}>
      <div style={shellCard}>
        <div style={headerRow}>
          <div>
            <h1 style={titleStyle}>Deliver items from storage</h1>
            <div style={subtitleStyle}>
              Select the items you want delivered from your storage unit to your
              address.
            </div>
          </div>
          <div style={sortBlock}>
            <label htmlFor="delivery-sort" style={sortLabel}>
              Sort by
            </label>
            <select
              id="delivery-sort"
              style={sortSelect}
              value={sortBy}
              onChange={e => setSortBy(e.target.value as SortOption)}
            >
              <option value="Newest">Date Added (Newest)</option>
              <option value="Alphabetical">A → Z</option>
            </select>
            <button
              type="button"
              style={refreshButton}
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              {isRefreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>

        {items.length === 0 ? (
          <div style={emptyState}>
            You don&apos;t have any items stored with Bins yet.
            <br />
            Once items are moved into storage, they&apos;ll show up here.
          </div>
        ) : (
          <div style={grid}>
            {items.map(item => {
              const isSelected = selectedIds.has(item.id);
              return (
                <div key={item.id} style={itemCard}>
                  <div style={imageWrapper}>
                    <img
                      style={imageStyle}
                      src={`${S3url}${item.photo}`}
                      alt={item.description}
                    />
                  </div>
                  <div style={itemBody}>
                    <div style={itemTitle}>
                      {item.description?.slice(0, 12) || 'Untitled item'}
                    </div>
                    <div style={itemMetaRow}>
                      <div style={itemMeta}>
                        ID #{item.id?.slice(0, 5) || 'N/A'}
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleSelect(item.id)}
                        style={{
                          ...selectButton,
                          ...(isSelected
                            ? selectButtonSelected
                            : selectButtonDefault),
                        }}
                      >
                        {isSelected ? 'Selected' : 'Select'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={footer}>
          <div style={selectionSummary}>
            {selectedIds.size === 0
              ? 'No items selected yet.'
              : `${selectedIds.size} item${
                  selectedIds.size > 1 ? 's' : ''
                } selected.`}
          </div>
          <button type="button" style={primaryButton} onClick={handleSubmit}>
            Deliver selected items to me
          </button>
        </div>
      </div>
    </div>
  );
};

export default TenantNewDeliveryPage;
